import { cardPayOnlineLabel, formatCents, formatCount, hasLiveFiskalDocument, qrPayLabel, tt } from '@agos/shared'

/**
 * §9.6's driver wallet — *what I took today, and what of it I hand over*.
 *
 * The port of `artifacts/driver-app/app/(tabs)/wallet.tsx` (1 176 lines). One endpoint feeds all of it:
 * `GET /employees/my-wallet` answers the employee, their vehicle, six lifetime totals and **every** order
 * they have ever carried — 1 841 rows, 40 045,35 €, for the driver in this data.
 *
 * **FB-185, three things the measurement found, all in the same shape: a number that is not what its name
 * says.**
 *
 * 1. **`cardTotal` is not the card total.** `WalletService` builds it from
 *    `Set.of("card_terminal", "card_online", "card")` — `card_courier` is **not** in that set. For this
 *    driver `cardTotal` is 8 110,55 € while `cardCourierTotal` alone is 15 870,80 €. So a *cash vs card*
 *    pair built from the two headline fields understates the card side by two thirds. Legacy sidesteps
 *    this by recomputing everything client-side and never reading the field; anything else falls in. This
 *    module never prints `cardTotal`. It names the four methods, and where it needs one figure it is the
 *    cash — see below for why that is the cash and nothing else.
 * 2. **The list is not completed orders.** `COUNTED` is `status NOT IN ('cancelled', 'pending')`, which is
 *    deliberate and documented in the service (a waitress mid-shift once saw less than her manager did).
 *    But it means `new` and `preparing` orders sit in the wallet — 2 of them right now, 38,35 € — while
 *    legacy's empty state promises *"Tu sa zobrazia vaše dokončené objednávky"*. Lifetime that is noise;
 *    on a *today* pill, an order still in the oven is a real part of a small number. So the rows say
 *    which ones are not delivered yet, and the section is called what it is.
 * 3. **The list and the total disagree by design.** `orders` carries pending online payments; `walletTotal`
 *    excludes them, because *"an online card payment nobody has confirmed is not money"*. Recomputing a
 *    day total from the list — which is exactly what legacy does for every day pill — therefore counts
 *    money the server does not. One such order exists today (28,35 €, no driver), so the visible
 *    difference is zero; the divergence is structural, not rare. {@link countsAsMoney} applies the
 *    server's own rule, and {@link withheldNote} says so when it has withheld something, because a total
 *    that silently drops a row is the same bug wearing the other coat.
 *
 * **What is actually handed over is the cash, and only the cash.** `HandoverReconciliation` says it in as
 * many words: *"Everything the person did not end up holding comes off the day's takings: money paid
 * online never reached them, the card terminal's batch is already at the bank…"* A card payment taken at
 * the door is collected by the driver but never held by them — it settles to the restaurant's account. The
 * 363 recorded handovers agree: of the 25 where both card kinds are non-zero, the driver's typed card
 * deduction equals `terminal + courier` in **17** and equals just one of them in **none**.
 *
 * And that deduction is deliberately the figure **read off the terminal**, not the one the orders imply —
 * *"those two disagree when a payment was taken outside the system, and the terminal is what the bank will
 * settle."* So this screen states the cash it can see and leaves the rest to the calculator, rather than
 * computing a handover figure out of card totals it has no business trusting.
 *
 * Everything here is integer cents (§14.7) and every day boundary is the device's own midnight — a driver
 * finishing at 01:00 is asking about the shift they just worked, not about UTC.
 */

export interface WalletOrder {
  id: number
  orderNumber: string
  status: string
  totalCents: number
  paymentMethod: string
  paymentStatus: string | null
  customerName: string | null
  customerAddress: string | null
  createdAt: string | null
  completedAt: string | null
  isPreorder: boolean
  scheduledAt: string | null
  /**
   * The register's two stamps - §9.568, FB-863.
   *
   * `WalletService` has answered both on every wallet read since §10A.293, so the driver's sheet can
   * say how much of what they are handing over the register has a live document for - which is the
   * subset `FiskalClosureService.takingsSince` counts the drawer against.
   */
  fiskalPrintedAt: string | null
  fiskalStornoAt: string | null
  /**
   * What this order pays an external driver, or null - BACKEND.md §10A.450, owner O151.
   *
   * The reward stored on the order, else his rates; the server answers it only for a delivered order of an employee
   * marked external, so a null here is *no pay to show*, never *nought*.
   */
  rewardCents: number | null
}

export interface WalletOwner {
  name: string
  position: string | null
  showWallet: boolean
  isExternal: boolean
  lifetimeCents: number
  lifetimeOrders: number
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function euros(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value * 100)
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0
  }
  return 0
}

export function ownerOf(payload: unknown): WalletOwner | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  if (typeof row.id !== 'number') return null
  return {
    name: text(row.name) ?? '',
    position: text(row.position),
    // The server sends `true` when the column is null, so absence is not a reason to hide anything.
    showWallet: row.showWallet !== false,
    isExternal: row.isExternal === true,
    lifetimeCents: euros(row.walletTotal),
    lifetimeOrders: typeof row.orderCount === 'number' ? row.orderCount : 0,
  }
}

export function walletOrdersOf(payload: unknown): WalletOrder[] {
  const rows = (payload as { orders?: unknown })?.orders
  if (!Array.isArray(rows)) return []
  return rows.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    return [
      {
        id: row.id,
        orderNumber: text(row.orderNumber) ?? String(row.id),
        status: text(row.status) ?? '',
        totalCents: euros(row.total),
        paymentMethod: text(row.paymentMethod) ?? '',
        paymentStatus: text(row.paymentStatus),
        customerName: text(row.customerName),
        customerAddress: text(row.customerAddress),
        createdAt: text(row.createdAt),
        completedAt: text(row.completedAt),
        isPreorder: row.isPreorder === true,
        scheduledAt: text(row.scheduledAt),
        fiskalPrintedAt: text(row.fiskalPrintedAt),
        fiskalStornoAt: text(row.fiskalStornoAt),
        rewardCents:
          typeof row.driverReward === 'number' && Number.isFinite(row.driverReward) ? euros(row.driverReward) : null,
      },
    ]
  })
}

// ─── Which day an order belongs to ───────────────────────────────────────────

/**
 * The day a driver would file this order under.
 *
 * A preorder taken on Monday for Saturday is Saturday's work, so legacy keys it on `scheduledAt` — the one
 * piece of its date handling that is right, and kept. Everything else here is local: the boundary is the
 * device's own midnight, because a driver finishing at 01:00 means the shift they just worked.
 */
export function effectiveStamp(order: WalletOrder): string | null {
  return order.isPreorder && order.scheduledAt !== null ? order.scheduledAt : order.createdAt
}

export function dayKeyOf(order: WalletOrder): string | null {
  const stamp = effectiveStamp(order)
  if (stamp === null) return null
  const parsed = new Date(stamp)
  if (Number.isNaN(parsed.getTime())) return null
  return localDay(parsed)
}

function localDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/**
 * `Dnes` / `Včera` / `dd.mm.` — with `today` injected, per FB-171.
 *
 * A default of "now" read at module load is what made the dashboard's ranges wrong when a tab stayed open
 * across midnight, and it makes a test that passes at 23:59 fail at 00:01.
 */
export function dayLabel(key: string, today: Date): string {
  if (key === localDay(today)) return tt('Dnes')
  const yesterday = new Date(today.getTime())
  yesterday.setDate(yesterday.getDate() - 1)
  if (key === localDay(yesterday)) return tt('Včera')
  const [, month, day] = key.split('-')
  return `${day ?? ''}.${month ?? ''}.`
}

// ─── What counts as money ────────────────────────────────────────────────────

/**
 * The server's own rule, applied to a client-side sum.
 *
 * `WalletService`: *"An online card payment nobody has confirmed is not money. It stays in the list — the
 * order happened — but it is not counted, because the alternative is a handover short by its value."* The
 * row stays visible here for the same reason; it just does not join the total.
 */
export function countsAsMoney(order: WalletOrder): boolean {
  if (order.paymentMethod !== 'card_online' && order.paymentMethod !== 'card') return true
  return order.paymentStatus === 'online_paid'
}


/** An order that is in the wallet but has not been delivered yet — `new`, `preparing`, `on_the_way`. */
export function isOutstanding(order: WalletOrder): boolean {
  return order.status !== 'delivered'
}

export interface DayTotals {
  count: number
  totalCents: number
  cashCents: number
  courierCents: number
  terminalCents: number
  onlineCents: number
  /**
   * A QR payment taken at the door - §9.741, owner O112. **The fifth bucket**, because §10A.390 files a paid
   * door QR payment as `qr_pay` and until now such an order was in `totalCents` and in none of the four - so
   * the split stopped adding up to the total the moment the first one arrived. Always counted, as the
   * server's own `walletTotal` counts it - `pendingOnline` withholds only `card_online` - because the door
   * flow writes the method on the transition to paid, so there is no unconfirmed one to withhold.
   */
  qrPayCents: number
  withheldCents: number
  withheldCount: number
  outstandingCount: number
}

// A plain integer reduce: `sumCents` parses its inputs and would multiply cents by a hundred (FB-056).
function sum(orders: readonly WalletOrder[]): number {
  return orders.reduce((total, order) => total + order.totalCents, 0)
}

function where(orders: readonly WalletOrder[], method: string): number {
  return sum(orders.filter((order) => order.paymentMethod === method))
}

export function dayTotals(orders: readonly WalletOrder[]): DayTotals {
  const counted = orders.filter(countsAsMoney)
  const withheld = orders.filter((order) => !countsAsMoney(order))
  return {
    count: orders.length,
    totalCents: sum(counted),
    cashCents: where(counted, 'cash'),
    courierCents: where(counted, 'card_courier'),
    terminalCents: where(counted, 'card_terminal'),
    onlineCents: sum(
      counted.filter((order) => order.paymentMethod === 'card_online' || order.paymentMethod === 'card'),
    ),
    qrPayCents: where(counted, 'qr_pay'),
    withheldCents: sum(withheld),
    withheldCount: withheld.length,
    outstandingCount: orders.filter(isOutstanding).length,
  }
}

export interface WalletDay {
  key: string
  totalCents: number
  count: number
}

/** The days that have orders, newest first, each with the figure the pill shows. */
export function daysOf(orders: readonly WalletOrder[]): WalletDay[] {
  const byDay = new Map<string, WalletOrder[]>()
  for (const order of orders) {
    const key = dayKeyOf(order)
    if (key === null) continue
    const bucket = byDay.get(key)
    if (bucket === undefined) byDay.set(key, [order])
    else bucket.push(order)
  }
  return [...byDay.entries()]
    .sort(([left], [right]) => right.localeCompare(left))
    .map(([key, rows]) => ({ key, totalCents: dayTotals(rows).totalCents, count: rows.length }))
}

/** The key of the device's own today, the day `/wallet` opens on - §9.804, owner O151. */
export function todayKey(today: Date): string {
  return localDay(today)
}

/** One pill: a day, or *Všetko*. */
export type WalletPill = { kind: 'day'; day: WalletDay } | { kind: 'all' }

/**
 * The pills in the order they are drawn - §9.804, owner O151: *"ukazovať prvé dnešný deň, nie Všetko"*.
 *
 * **Today first, and always**, with nought on a day that has no orders yet: it is the day the screen opens on, and a
 * pill that appeared only after the first delivery would leave the opening state unmarked. Then *Všetko*, then the
 * older days newest first. *Všetko* is second rather than last because `my-wallet` answers a whole career, so the
 * end of the row can be hundreds of days away.
 */
export function pillsOf(days: readonly WalletDay[], today: string): WalletPill[] {
  const own = days.find((entry) => entry.key === today) ?? { key: today, totalCents: 0, count: 0 }
  return [
    { kind: 'day', day: own },
    { kind: 'all' },
    ...days.filter((entry) => entry.key !== today).map((day) => ({ kind: 'day' as const, day })),
  ]
}

export function ordersOnDay(orders: readonly WalletOrder[], day: string | null): WalletOrder[] {
  if (day === null) return [...orders]
  return orders.filter((order) => dayKeyOf(order) === day)
}

// ─── What the screen says ────────────────────────────────────────────────────

/**
 * What the driver is holding, and what still has to come off it.
 *
 * Not the day total: an online payment never reached them and a card taken at the door settled to the
 * restaurant. What is in the bag is the cash — and the amount actually handed over is that minus fuel,
 * purchases and payouts, which is the calculator's job and not this screen's.
 */
export function cashNote(totals: DayTotals): string {
  // §9.767, owner O129 - the sentence that says why the headline is only the cash names CardPay online and QR Pay
  // always, by their names, because both lines now sit in the split whatever they hold.
  const elsewhere = tt(
    'Karta, CardPay online a QR Pay idú priamo reštaurácii - neodovzdávate ich. Tankovanie, nákupy a výplaty si odrátate v kalkulačke.',
  )
  return `${tt('V hotovosti máte')} ${formatCents(totals.cashCents)}. ${elsewhere}`
}

/** Said only when a row was left out, because a total that silently drops one is worse than no total. */
export function withheldNote(totals: DayTotals): string {
  if (totals.withheldCount === 0) return ''
  return `${formatCount(totals.withheldCount)} ${tt('objednávok sa nezapočítava')} (${formatCents(totals.withheldCents)}) - ${tt('online platbu nikto nepotvrdil, takže to zatiaľ nie sú peniaze. V zozname zostávajú.')}`
}

/** Said only when something is still open, because on a single day that is a real part of the figure. */
export function outstandingNote(totals: DayTotals): string {
  if (totals.outstandingCount === 0) return ''
  return `${formatCount(totals.outstandingCount)} ${tt('z týchto objednávok ešte nie je doručených - sú v sume, lebo sú vaše, ale ešte ich nemáte za sebou.')}`
}

export function lifetimeNote(owner: WalletOwner): string {
  return `${tt('Celkovo za celý čas')}: ${formatCents(owner.lifetimeCents)} ${tt('z')} ${formatCount(owner.lifetimeOrders)} ${tt('objednávok. To nie je dnešná tržba.')}`
}

/**
 * `show_wallet` is off.
 *
 * The server still sends every order — it hides nothing — so the hiding is the client's job, and saying
 * *why* is what stops a driver assuming the app is broken. Measured: **0 of 23** employees have it off, so
 * this state is unreachable in this data and is built from the flag, not from a fixture.
 */
export function hiddenNote(): string {
  return tt(
    'Správca vypol zobrazenie tržby a histórie objednávok pre váš účet. Ak to je omylom, povie vám to on - nie je to chyba aplikácie.',
  )
}

export function methodLabel(paymentMethod: string): string {
  if (paymentMethod === 'cash') return tt('Hotovosť')
  if (paymentMethod === 'card_courier') return tt('Karta u kuriéra')
  if (paymentMethod === 'card_terminal') return tt('Terminál')
  // §9.767 - the online card's one name (it said *Online*); the legacy `card` is the online card in every handover.
  if (paymentMethod === 'card_online' || paymentMethod === 'card') return cardPayOnlineLabel()
  // §9.741 - the owner's word, and the one *Platby online - archív* uses. It drew the raw key before.
  if (paymentMethod === 'qr_pay') return qrPayLabel()
  return paymentMethod === '' ? tt('neuvedené') : paymentMethod
}

export function statusNote(order: WalletOrder): string {
  if (!isOutstanding(order)) return ''
  if (order.status === 'on_the_way') return tt('na ceste')
  if (order.status === 'at_address') return tt('ide na adresu')
  if (order.status === 'preparing') return tt('ešte sa pripravuje')
  if (order.status === 'new') return tt('ešte neprevzatá')
  return order.status
}

/**
 * How much of a day's takings the register has a live document for - §9.568, FB-863.
 *
 * **The owner's ask on the driver's side of the same screen.** The uzávierka counts only orders whose
 * document is printed and not cancelled, so a handover figure that includes the rest cannot be
 * reconciled against it - and nothing on this sheet said which part was which.
 *
 * <p>Cents rather than a formatted string, because the caller has the formatter and §9.568's own note
 * in `manager-orders` needs the same two numbers. `hasLiveFiskalDocument` is shared, so the driver's
 * sheet and the waiter's modal cannot disagree about what *registered* means.
 */
export function documentedCentsOf(orders: readonly WalletOrder[]): number {
  return orders
    .filter((order) => hasLiveFiskalDocument(order))
    .reduce((sum, order) => sum + order.totalCents, 0)
}

/** *Fiskal Hotovosť* and *Fiskal Karta* for a driver's day - §9.571, owner instruction 2026-09-28. */
export function documentedSplitOf(orders: readonly WalletOrder[]): {
  cashCents: number
  cardCents: number
} {
  const registered = orders.filter((order) => hasLiveFiskalDocument(order))
  const isCash = (order: WalletOrder) => order.paymentMethod === 'cash'
  return {
    cashCents: registered.filter(isCash).reduce((sum, order) => sum + order.totalCents, 0),
    cardCents: registered.filter((order) => !isCash(order)).reduce((sum, order) => sum + order.totalCents, 0),
  }
}

// ─── An external driver's pay - §9.804, owner O151 ─────────────────────────────

/** What the shown orders pay an external driver, and how many of them carry pay. */
export function payOf(orders: readonly WalletOrder[]): { rewardCents: number; count: number } {
  const paid = orders.filter((order) => order.rewardCents !== null)
  return { rewardCents: paid.reduce((total, order) => total + (order.rewardCents ?? 0), 0), count: paid.length }
}

/**
 * How the day's cash splits between the restaurant and an external driver - §10A.438 E's netting.
 *
 * He keeps his pay out of the cash he collected; an order the customer paid online is pay the restaurant owes him.
 * So the cash less the pay is the restaurant's when it is positive, and his to be given when it is negative.
 */
export function payNote(cashCents: number, rewardCents: number): string {
  const net = cashCents - rewardCents
  if (net > 0) return `${tt('Prevádzke patrí')} ${formatCents(net)}`
  if (net < 0) return `${tt('Prevádzka vám dá')} ${formatCents(-net)}`
  return tt('Vyrovnané - nikto nikomu nič nedlží.')
}

/** Where the takings calculator was, for an external driver: the server refuses his handover (§10A.450). */
export function externalSettleNote(): string {
  return tt(
    'Ako externý šofér sa s prevádzkou vyrovnávate osobne pri vyzdvihnutí - tu vidíte, koľko z hotovosti patrí prevádzke a koľko vám.',
  )
}
